import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import TradingView from "./TradingView";
import { getAccessToken } from "../utils/auth";
import { API_BASE_URL } from "../utils/api";
import { compareSwimmerObjectsByName } from "../utils/swimmerSort";
import MascotLogo from "./MascotLogo";

const SESSION_USER_KEY = "fantasySwimSessionUser";
const readSessionUser = () => {
  try {
    const raw = localStorage.getItem(SESSION_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const API = API_BASE_URL;

const authHeaders = () => ({
  Authorization: `Bearer ${getAccessToken()}`,
});

const TeamOverview = () => {
  const [lineup, setLineup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [captains, setCaptains] = useState({});

  const fetchLineup = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/team/lineup`, {
        headers: authHeaders(),
      });
      setLineup(res.data);
      setCaptains(res.data.captains || {});
      setStatus("");
    } catch (e) {
      setStatus("Error: " + (e.response?.data?.detail ?? e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLineup();
  }, [fetchLineup]);

  const updateCaptain = async (ageGroup, swimmerName) => {
    setStatus("");
    try {
      const res = await axios.patch(
        `${API}/team/captains`,
        { age_group: ageGroup, swimmer_name: swimmerName },
        { headers: authHeaders() },
      );
      setLineup(res.data);
      setCaptains(res.data.captains || {});
      setStatus("Captain updated.");
    } catch (e) {
      setStatus("Error: " + (e.response?.data?.detail ?? e.message));
    }
  };

  const allSwimmers = useMemo(() => {
    const swimmers = [];
    (lineup?.active_slots || []).forEach((slot) => {
      if (slot?.swimmer) swimmers.push(slot.swimmer);
    });
    (lineup?.bench || []).forEach((swimmer) => {
      swimmers.push(swimmer);
    });
    return swimmers.sort((a, b) => compareSwimmerObjectsByName(a, b, "name"));
  }, [lineup]);

  if (loading) {
    return <p>Loading lineup…</p>;
  }

  if (!lineup) {
    return <p>{status || "Unable to load lineup."}</p>;
  }

  return (
    <section style={styles.contentCard}>
      <div style={styles.overviewHeader}>
        <div>
          <h2 style={styles.cardTitle}>Team Roster</h2>
          <p style={styles.helperText}>
            All swimmers on your team score points for placement and time drops.
          </p>
        </div>
        <div
          style={
            lineup.locked
              ? { ...styles.lockBadge, ...styles.lockedBadge }
              : styles.lockBadge
          }
        >
          {lineup.locked ? "Trading Closed" : "Trading Open"}
        </div>
      </div>

      <div style={styles.summaryGrid}>
        <div style={styles.summaryCard}>
          <strong>{lineup.team_size || 0}</strong>
          <span>Team Swimmers</span>
        </div>
      </div>

      {status ? (
        <p
          style={
            status.startsWith("Error") ? styles.errorText : styles.successText
          }
        >
          {status}
        </p>
      ) : null}

      {lineup.locked ? (
        <div style={styles.noticeBox}>
          The game is locked right now. Active and bench assignments cannot be
          changed until an admin unlocks it.
        </div>
      ) : null}

      <div style={styles.captainsSection}>
        <h2 style={styles.cardTitle}>Captains</h2>
        <p style={styles.helperText}>
          Select one captain per age group. Captains score 1.5 bonus points for
          your team.
        </p>
        {["6u", "7-8", "9-10"].map((age) => {
          const candidates = allSwimmers.filter((s) => s.age_group === age);
          return (
            <div key={age} style={styles.captainRow}>
              <label style={styles.captainLabel}>{age} Captain:</label>
              <select
                value={captains[age] || ""}
                onChange={(e) => updateCaptain(age, e.target.value || null)}
                style={styles.select}
              >
                <option value="">No captain</option>
                {candidates.map((s) => (
                  <option key={s.name} value={s.name}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>

      <div style={styles.benchSection}>
        <div style={styles.benchHeaderRow}>
          <h2 style={styles.cardTitle}>Team Swimmers</h2>
          <span style={styles.benchCount}>{allSwimmers.length} swimmers</span>
        </div>

        {allSwimmers.length === 0 ? (
          <p style={styles.helperText}>No swimmers on your team yet.</p>
        ) : (
          <div style={styles.benchList}>
            {allSwimmers.map((swimmer) => (
              <div key={swimmer.name} style={styles.benchPill}>
                <span>{swimmer.name}</span>
                <small style={styles.benchMeta}>
                  {swimmer.age_group} ·{" "}
                  {swimmer.gender === "F" ? "Girl" : "Boy"}
                </small>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const TeamView = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const inTrading = location.pathname === "/team/trading";

  const sessionUser = readSessionUser();
  const mascotImage = sessionUser?.profileImageDataUrl || "";
  const mascotName = sessionUser?.coachName || sessionUser?.displayName || "Team";

  return (
    <div style={styles.page}>
      <h1 className="page-title">Team</h1>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "0.4rem",
          margin: "0.5rem 0 1rem 0",
        }}
      >
        <MascotLogo
          imageDataUrl={mascotImage}
          coachName={mascotName}
          size={180}
          borderRadius="14px"
        />
        <div
          style={{
            fontSize: "1.05rem",
            fontWeight: 700,
            color: "#cddcf8",
            letterSpacing: "0.02em",
          }}
        >
          {mascotName}
        </div>
      </div>

      <div style={styles.actionRow} className="sub-nav-bar">
        <button
          type="button"
          aria-pressed={!inTrading}
          onClick={() => navigate("/team")}
          style={
            !inTrading
              ? { ...styles.actionBtn, ...styles.actionBtnActive }
              : styles.actionBtn
          }
        >
          🏠 Overview
        </button>
        <button
          type="button"
          aria-pressed={inTrading}
          onClick={() => navigate("/team/trading")}
          style={
            inTrading
              ? { ...styles.actionBtn, ...styles.actionBtnActive }
              : styles.actionBtn
          }
        >
          🔁 Trading
        </button>
      </div>

      {!inTrading ? (
        <TeamOverview />
      ) : (
        <section style={styles.contentCard}>
          <TradingView user={user} />
        </section>
      )}
    </div>
  );
};

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    gap: "0.55rem",
  },
  actionRow: {
    display: "flex",
    gap: "0.35rem",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "0",
    borderRadius: "0",
    border: "none",
    backgroundColor: "transparent",
  },
  actionBtn: {
    border: "none",
    backgroundColor: "rgba(7, 20, 60, 0.45)",
    color: "#a6bce6",
    borderRadius: "9px",
    padding: "0.44rem 0.7rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    fontSize: "0.73rem",
    whiteSpace: "nowrap",
    boxShadow: "0 7px 18px rgba(0, 0, 0, 0.26)",
  },
  actionBtnActive: {
    color: "#02061a",
    backgroundColor: "#00f5d4",
    boxShadow: "0 0 14px rgba(0,245,212,0.3)",
  },
  contentCard: {
    border: "1px solid rgba(0,245,212,0.34)",
    borderRadius: "14px",
    padding: "0.58rem",
    backgroundColor: "rgba(7, 20, 60, 0.72)",
    boxShadow: "inset 0 0 0 1px rgba(0,245,212,0.08)",
  },
  cardTitle: {
    marginTop: 0,
    marginBottom: "0.3rem",
    fontSize: "1.5rem",
    fontWeight: 800,
    color: "#eaf3ff",
  },
  helperText: {
    margin: 0,
    color: "#9fb7e3",
    fontSize: "0.88rem",
  },
  overviewHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: "0.75rem",
    flexWrap: "wrap",
    alignItems: "flex-start",
    marginBottom: "0.9rem",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: "0.6rem",
    marginBottom: "0.9rem",
  },
  summaryCard: {
    display: "flex",
    flexDirection: "column",
    gap: "0.18rem",
    padding: "0.75rem",
    borderRadius: "12px",
    border: "1px solid rgba(75, 112, 191, 0.35)",
    backgroundColor: "rgba(9, 23, 64, 0.82)",
    color: "#dceaff",
  },
  lockBadge: {
    padding: "0.45rem 0.7rem",
    borderRadius: "999px",
    border: "1px solid rgba(0,245,212,0.55)",
    backgroundColor: "rgba(0,245,212,0.12)",
    color: "#a8fff0",
    fontWeight: 800,
    fontSize: "0.8rem",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  lockedBadge: {
    borderColor: "rgba(255, 126, 160, 0.65)",
    backgroundColor: "rgba(255, 126, 160, 0.14)",
    color: "#ffc1d3",
  },
  noticeBox: {
    marginBottom: "0.9rem",
    padding: "0.75rem 0.85rem",
    borderRadius: "12px",
    border: "1px solid rgba(255, 126, 160, 0.55)",
    backgroundColor: "rgba(255, 126, 160, 0.1)",
    color: "#ffd8e3",
    fontWeight: 600,
  },
  slotGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "0.7rem",
  },
  ageGroupCard: {
    border: "1px solid rgba(75, 112, 191, 0.4)",
    borderRadius: "12px",
    padding: "0.72rem",
    backgroundColor: "rgba(6, 20, 58, 0.82)",
    boxShadow: "inset 0 0 0 1px rgba(75, 112, 191, 0.16)",
  },
  ageGroupTitle: {
    margin: "0 0 0.52rem",
    color: "#eaf3ff",
    fontSize: "0.98rem",
    fontWeight: 800,
    letterSpacing: "0.02em",
  },
  ageGroupSlotGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "0.7rem",
  },
  slotCard: {
    border: "1px solid rgba(75, 112, 191, 0.35)",
    borderRadius: "12px",
    padding: "0.8rem",
    backgroundColor: "rgba(9, 23, 64, 0.84)",
    display: "flex",
    flexDirection: "column",
    gap: "0.55rem",
  },
  slotCardFilled: {
    border: "1px solid rgba(0, 245, 212, 0.72)",
    backgroundColor: "rgba(0, 245, 212, 0.14)",
    boxShadow: "inset 0 0 0 1px rgba(0, 245, 212, 0.22)",
  },
  slotHeader: {
    display: "flex",
    flexDirection: "column",
    gap: "0.12rem",
  },
  slotTitleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.45rem",
  },
  slotTitle: {
    margin: 0,
    fontSize: "1rem",
    color: "#eaf3ff",
  },
  filledBadge: {
    border: "1px solid rgba(0,245,212,0.72)",
    backgroundColor: "rgba(4, 24, 56, 0.9)",
    color: "#a8fff0",
    borderRadius: "999px",
    padding: "0.14rem 0.48rem",
    fontSize: "0.68rem",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    whiteSpace: "nowrap",
  },
  slotMeta: {
    color: "#8fb0e7",
    fontSize: "0.78rem",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  slotMetaFilled: {
    color: "#d6fffa",
  },
  select: {
    width: "100%",
    borderRadius: "10px",
    border: "1px solid rgba(0,245,212,0.38)",
    backgroundColor: "rgba(5, 17, 51, 0.92)",
    color: "#f1f6ff",
    padding: "0.62rem 0.72rem",
    fontSize: "0.92rem",
  },
  selectFilled: {
    border: "1px solid rgba(0,245,212,0.78)",
    backgroundColor: "rgba(4, 24, 56, 0.95)",
    color: "#a8fff0",
  },
  slotFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.5rem",
  },
  currentSwimmer: {
    color: "#ffffff",
    fontSize: "1.08rem",
    fontWeight: 800,
  },
  currentSwimmerFilled: {
    color: "#ffffff",
  },
  clearBtn: {
    border: "1px solid rgba(255, 126, 160, 0.65)",
    borderRadius: "9px",
    backgroundColor: "rgba(255, 126, 160, 0.14)",
    color: "#ffc1d3",
    fontWeight: 700,
    padding: "0.42rem 0.68rem",
    cursor: "pointer",
  },
  benchSection: {
    marginTop: "1rem",
  },
  benchHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.6rem",
    flexWrap: "wrap",
  },
  benchCount: {
    color: "#8fb0e7",
    fontSize: "0.82rem",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    fontWeight: 700,
  },
  benchList: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.55rem",
    marginTop: "0.75rem",
  },
  benchPill: {
    display: "flex",
    flexDirection: "column",
    gap: "0.12rem",
    padding: "0.58rem 0.72rem",
    borderRadius: "12px",
    border: "1px solid rgba(75, 112, 191, 0.35)",
    backgroundColor: "rgba(5, 17, 51, 0.9)",
    color: "#eaf3ff",
    minWidth: "160px",
  },
  benchMeta: {
    color: "#8fb0e7",
    fontSize: "0.74rem",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  successText: {
    color: "#a8fff0",
    fontWeight: 700,
  },
  errorText: {
    color: "#ffc1d3",
    fontWeight: 700,
  },
  primaryBtn: {
    border: "1px solid rgba(0,245,212,0.7)",
    borderRadius: "10px",
    backgroundColor: "rgba(0,245,212,0.14)",
    color: "#a9fff0",
    fontWeight: 700,
    padding: "0.5rem 0.85rem",
    cursor: "pointer",
  },
  captainsSection: {
    marginTop: "1rem",
  },
  captainRow: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    marginBottom: "0.5rem",
  },
  captainLabel: {
    minWidth: "100px",
    fontWeight: "bold",
  },
};

export default TeamView;
